import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { CheckCircle2, ChevronRight, X, Trophy, Users, Calendar, PlayCircle, ArrowRight } from 'lucide-react'

export const TournamentAssistant = () => {
    const { tournamentId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [stats, setStats] = useState({ poules: 0, teams: 0, matches: 0, started: false })
    const [loading, setLoading] = useState(true)
    const [closed, setClosed] = useState(false)

    // Alleen tonen als we binnen een toernooi zitten (route check op meervoud tournaments)
    const isTournamentRoute = location.pathname.includes('/admin/tournaments/')

    // Haal progressie op -> Hook moet ALTIJD aangeroepen worden
    useEffect(() => {
        // Als we niet in een toernooi zitten of geen ID hebben, doen we niets
        if (!tournamentId || !isTournamentRoute) {
            setLoading(false)
            return
        }

        const fetchProgress = async () => {
            try {
                // Check Poules
                const { count: pouleCount } = await supabase
                    .from('poules')
                    .select('*', { count: 'exact', head: true })
                    .eq('tournament_id', tournamentId)

                // Check Teams
                const { count: teamCount } = await supabase
                    .from('teams')
                    .select('*', { count: 'exact', head: true })
                    .eq('tournament_id', tournamentId)

                // Check Matches
                const { count: matchCount } = await supabase
                    .from('matches')
                    .select('*', { count: 'exact', head: true })
                    .eq('tournament_id', tournamentId)

                setStats({
                    poules: pouleCount || 0,
                    teams: teamCount || 0,
                    matches: matchCount || 0
                })
            } catch (err) {
                console.error("Assistant Error:", err)
            } finally {
                setLoading(false)
            }
        }

        fetchProgress()

        // Poll every 5 seconds to keep stats fresh (e.g. after generating matches)
        const interval = setInterval(fetchProgress, 5000)
        return () => clearInterval(interval)

    }, [tournamentId, location.pathname, isTournamentRoute])

    // NU PAS de early returns (na alle hooks)
    if (!tournamentId || !isTournamentRoute) return null
    if (closed) return null
    if (loading) return null

    // Bepaal de Huidige Stap
    let step = 0
    let title = ""
    let description = ""
    let action = () => { }
    let btnText = ""

    // JUISTE ROUTES GEBRUIKEN (tournaments meervoud!)
    if (stats.poules === 0 && stats.teams === 0) {
        step = 1
        title = "Stap 1: Structuur Opzetten"
        description = "Begin met het maken van poules of divisies."
        btnText = "Naar Divisies"
        action = () => navigate(`/admin/tournaments/${tournamentId}/divisions`)
    } else if (stats.teams === 0) {
        step = 2
        title = "Stap 2: Deelnemers Toevoegen"
        description = "Voeg teams of spelers toe aan het toernooi."
        btnText = "Naar Teams"
        action = () => navigate(`/admin/tournaments/${tournamentId}/teams`)
    } else if (stats.matches === 0) {
        step = 3
        title = "Stap 3: Wedstrijdschema"
        description = "Alles klaar? Genereer het schema met één klik."
        btnText = "Naar Wedstrijden"
        action = () => {
            // If already on matches page, just refresh stats to hopefully dismiss banner
            if (location.pathname.endsWith('/matches')) {
                // Force re-fetch manually just in case interval hasn't hit yet
                setLoading(true)
                // logic will follow in effect
            } else {
                navigate(`/admin/tournaments/${tournamentId}/matches`)
            }
        }
    } else {
        // Klaar! Geen balk meer nodig
        return null
    }

    // NIEUW DESIGN: Duidelijke Balk Bovenaan (onder de header)
    // We gebruiken 'fixed' positioning onder de sidebar/header ruimte
    return (
        <div className="absolute top-0 left-0 right-0 z-30 animate-slideDown">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg p-3 md:px-6 flex flex-col md:flex-row items-center justify-between gap-3 border-b border-white/10">

                {/* Links: Voortgang */}
                <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 font-bold border-2 border-white/50">
                        {step}/3
                    </div>
                    <div>
                        <h3 className="font-bold text-lg leading-tight">{title}</h3>
                        <p className="text-blue-100 text-sm hidden md:block">{description}</p>
                    </div>
                </div>

                {/* Midden: Progress Bar (Visueel) */}
                <div className="hidden md:flex gap-1 items-center mx-4">
                    <div className={`h-2 w-16 rounded-full ${step >= 1 ? 'bg-green-400' : 'bg-white/20'}`} />
                    <div className={`h-2 w-16 rounded-full ${step >= 2 ? 'bg-green-400' : 'bg-white/20'}`} />
                    <div className={`h-2 w-16 rounded-full ${step >= 3 ? 'bg-green-400' : 'bg-white/20'}`} />
                </div>

                {/* Rechts: Actie Knop */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={action}
                        className="py-2 px-5 bg-white text-blue-700 font-bold rounded-lg shadow hover:bg-blue-50 hover:scale-105 transition-all flex items-center gap-2"
                    >
                        {btnText} <ArrowRight size={18} />
                    </button>
                    <button
                        onClick={() => setClosed(true)}
                        className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
                        title="Verberg Assistent"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>
        </div>
    )
}
